import {
  FileStack,
  Scan,
  FileJson,
  Library,
  Download,
  ImageDown,
  Archive,
  Camera,
  HardDriveDownload,
  Eye,
  EyeOff,
  Upload,
} from 'lucide-react';
import type { FeatureGroup } from '../types';

export const filesGroup: FeatureGroup = {
  id: 'files',
  label: 'Files & Export',
  headline: 'A full file system, in the browser',
  summary:
    'Attach any file to a model — SVGs, DXFs, JSON data, STLs, textures, shader scripts. Export in multiple formats with full multi-color support. Pre-installed libraries mean you never need to copy-paste dependencies again.',
  accentClass: 'from-teal-500 to-emerald-400',
  items: [
    {
      icon: FileStack,
      title: 'Multi-file Projects',
      description:
        'Attach .svg, .dxf, .stl, .dat, .json, .js, and more alongside your main .scad file. Files in the panel are fully editable inline. Create new files from initialization templates.',
      tier: 'major',
    },
    {
      icon: Scan,
      title: 'SVG Splitting',
      description:
        'Import an SVG and split it into individual path files automatically — each group becomes its own file, ready to reference independently in code.',
      tier: 'major',
    },
    {
      icon: FileJson,
      title: 'JSON & API Imports',
      description:
        'Import .json files directly into your model, or fetch live external APIs to drive data-driven and generative designs — parametric models powered by real-world data.',
      tier: 'major',
    },
    {
      icon: Library,
      title: 'Bundled Libraries',
      description:
        'BOSL2, BOSL, MCAD, LEGO.scad, and Gridfinity are pre-installed and importable in any model. No setup, no copying files — just use include.',
      tier: 'major',
    },
    {
      icon: Download,
      title: 'STL & SCAD Export',
      description:
        'Download your model as an STL at any time. Parameters are bundled with the export. Multi-color models export as a zip of per-color STL files, ready for multi-material printing.',
      tier: 'major',
    },
    {
      icon: ImageDown,
      title: 'High-Quality Export',
      description:
        'Export high-resolution screenshots and renders of your model. Configurable resolution and post-processing for presentations, portfolio use, or print-ready product imagery.',
      tier: 'major',
    },
    {
      icon: Archive,
      title: 'Multi-Color ZIP Export',
      description:
        'Every color in a model compiles in parallel across a four-worker pool. The result is a zip of per-color STL files — drop them straight into your slicer.',
      tier: 'major',
    },
    {
      icon: Camera,
      title: 'Screenshot',
      description:
        "Capture the current 3D viewport as a PNG with one shortcut (⌘⇧S). Optionally set the screenshot as the model's public thumbnail.",
      tier: 'minor',
    },
    {
      icon: HardDriveDownload,
      title: 'Mesh Capture to File System',
      description:
        "Save the current compiled mesh directly into the project's virtual file system as an STL — making it available for import() in other SCAD files in the same project.",
      tier: 'minor',
    },
    {
      icon: Eye,
      title: 'STL Preview',
      description:
        'Preview any .stl file in the 3D viewer directly from the file manager panel — inspect imported geometry without leaving the editor.',
      tier: 'minor',
    },
    {
      icon: EyeOff,
      title: 'Exclude-from-Export Modifier',
      description:
        'Prefix any geometry with ~ to make it visible in the preview but excluded from STL output — handy for jigs, reference geometry, and build-plate fixtures.',
      tier: 'minor',
    },
    {
      icon: Upload,
      title: 'Parameter Import',
      description:
        'Import a JSON file to bulk-set all parameter values at once — useful for restoring a saved configuration or batch-testing a set of parameter combinations.',
      tier: 'minor',
    },
  ],
};
